import { useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import { mockCaseReceivables } from '@/mocks/finance'
import { USE_MOCKS } from '@/mocks/fixtures'
import type { CaseReceivables, Receivable } from '@/types/domain'

/* One case's receivables by document (S-21): `GET /cases/:id/receivables`.
 *
 * Every issued invoice (its GROSS total), every issued debit note billed to the
 * case's own client, every tranche — those an issued invoice replaced included —
 * each with what has been paid against it and what is left, in its own currency
 * and in USD, and the server's word for where it stands. The figures are the
 * server's (AD-9): «Счета клиенту» prints them and the client payment sheet
 * checks typed amounts against them, and nothing here recomputes one.
 *
 * Gated by the case's reach alone, so the same payload answers Finance and the
 * Sales user on the deal; it names documents and amounts and nobody.
 *
 * `payIn` + `on` ask the server what each row can still take in a payment's
 * currency on its date (`outstandingInPayment`, floored to that currency's
 * minor unit so a split of exactly it is accepted). They travel together —
 * `payIn` without `on` is 422 — so this hook sends neither unless both are
 * known. Under `finance.all`, so every payment write and every issue refreshes
 * it.
 *
 * `keepPrevious` holds the SAME case's last answer on screen while a new
 * `payIn`/`on` is read (`isPlaceholderData`), so a form keyed by its own fields
 * does not lose its rows on every date change — another case's rows are never
 * shown in their place. Such a form must not act on those rows until the read
 * for its current fields has answered — see `ClientPaymentSheet`. */
export function useCaseReceivables(
  caseId: string,
  {
    payIn,
    on,
    enabled = true,
    keepPrevious = false,
  }: { payIn?: string; on?: string; enabled?: boolean; keepPrevious?: boolean } = {},
) {
  /* Both or neither: a payment date not yet typed asks the plain read. */
  const asked = payIn !== undefined && payIn !== '' && on !== undefined && on !== ''
  const params = asked ? { payIn, on } : undefined
  return useQuery<CaseReceivables>({
    queryKey: queryKeys.finance.receivables(caseId, params?.payIn, params?.on),
    // An empty id is a caller that resolved no case (or gated the read off).
    enabled: enabled && caseId !== '',
    placeholderData: (previous, previousQuery) =>
      keepPrevious && previousQuery?.queryKey[2] === caseId ? previous : undefined,
    queryFn: async () =>
      USE_MOCKS
        ? mockCaseReceivables(caseId, params?.payIn)
        : api.get<CaseReceivables>(`/cases/${caseId}/receivables`, { params }),
  })
}

/** The served row for one receivable, by kind and id — how a screen that lists
 *  a document from another read (the invoices, the debit notes, the schedule)
 *  finds what is paid and what is left on it. */
export function receivableOf(
  receivables: CaseReceivables | undefined,
  kind: Receivable['kind'],
  id: string,
): Receivable | undefined {
  return receivables?.items.find((row) => row.kind === kind && row.id === id)
}

/** What issuing an invoice will carry over from its tranche (S-21): the money
 *  already allocated to the tranche the invoice replaces moves onto the invoice
 *  in the issue's own transaction, re-derived against the invoice's booking. The
 *  issue confirmation says how much, before the irreversible press.
 *
 *  `undefined` when the invoice names no tranche, the tranche holds nothing, or
 *  the read has not answered — the confirmation then says what it always said. */
export function useTrancheCarryOver(
  caseId: string,
  trancheId: string | null | undefined,
  { enabled = true }: { enabled?: boolean } = {},
): Extract<Receivable, { kind: 'tranche' }> | undefined {
  const asked = enabled && trancheId !== null && trancheId !== undefined && trancheId !== ''
  const { data } = useCaseReceivables(caseId, { enabled: asked })
  if (!asked) return undefined
  const row = receivableOf(data, 'tranche', trancheId)
  if (row === undefined || row.kind !== 'tranche') return undefined
  // Read off the served decimal's digits, never parsed into a float (AD-9).
  return /[1-9]/.test(row.paid.amount) && !row.paid.amount.startsWith('-') ? row : undefined
}
